/**
 * IndexedDB for the offline-first console. Two stores:
 *  - `outbox`  — queued mutations (create/reschedule appointment, queue actions) that survive a
 *                Wi-Fi blip or reload and replay in order when the connection returns. The API's
 *                idempotency (CreateAppointment.idempotencyKey, ADR brief) makes replay safe.
 *  - `cache`   — a local read replica of today's diary + client basics so the app opens with data
 *                offline (brief WS2: "local replica … IndexedDB").
 */
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

export type OutboxKind =
  | 'appointment.create'
  | 'appointment.update'
  | 'queue.action'
  | 'queue.join';

export interface OutboxItem {
  id: string;
  kind: OutboxKind;
  /** The exact arguments for the API call (already validated client-side). */
  payload: unknown;
  /** Query keys to invalidate once this item lands on the server. */
  invalidate: ReadonlyArray<ReadonlyArray<unknown>>;
  createdAt: number;
  tries: number;
  lastError?: string;
}

export interface CacheEntry {
  key: string;
  data: unknown;
  updatedAt: number;
}

interface ConsoleDB extends DBSchema {
  outbox: { key: string; value: OutboxItem; indexes: { 'by-createdAt': number } };
  cache: { key: string; value: CacheEntry };
}

let dbPromise: Promise<IDBPDatabase<ConsoleDB>> | null = null;

export function getDb(): Promise<IDBPDatabase<ConsoleDB>> {
  if (!dbPromise) {
    dbPromise = openDB<ConsoleDB>('salonomy-console', 1, {
      upgrade(db) {
        const outbox = db.createObjectStore('outbox', { keyPath: 'id' });
        outbox.createIndex('by-createdAt', 'createdAt');
        db.createObjectStore('cache', { keyPath: 'key' });
      },
    });
  }
  return dbPromise;
}

// ── Outbox ──
export async function outboxAll(): Promise<OutboxItem[]> {
  const db = await getDb();
  return db.getAllFromIndex('outbox', 'by-createdAt');
}

export async function outboxPut(item: OutboxItem): Promise<void> {
  const db = await getDb();
  await db.put('outbox', item);
}

export async function outboxDelete(id: string): Promise<void> {
  const db = await getDb();
  await db.delete('outbox', id);
}

export async function outboxCount(): Promise<number> {
  const db = await getDb();
  return db.count('outbox');
}

// ── Read replica ──
export async function cacheGetAll(): Promise<CacheEntry[]> {
  const db = await getDb();
  return db.getAll('cache');
}

export async function cachePut(key: string, data: unknown): Promise<void> {
  const db = await getDb();
  await db.put('cache', { key, data, updatedAt: Date.now() });
}
